import React, { ComponentProps, FC, useCallback, useMemo } from 'react';
import { URLRulesPatterns } from './URLRulesPatterns';
import { PageDefinitionForm, PatternRuleForm } from '../../types';
import { useFlags } from '@fullstory/react-hooks';
import { AutocompleteRequest } from '@fullstory/proto/fs/services/paige';
import { validatePatterns } from '../../utils/form';

interface URLRulesWrapperProps
  extends Pick<
    ComponentProps<typeof URLRulesPatterns>,
    'onConflictingRulesEdit' | 'onConflictingRulesArchive' | 'errors' | 'getPageById'
  > {
  setForm: (form: PageDefinitionForm) => void;
  form: PageDefinitionForm;
  hostnameOptions: string[];
  getSuggestions: (
    path: string,
    fragment: string,
    hostname: string,
    scope: AutocompleteRequest.Scope,
  ) => Promise<string[]>;
  mode: string;
  isCaseInsensitive?: boolean;
  preventOverflow?: boolean;
}

export const URLRulesWrapper: FC<URLRulesWrapperProps> = ({
  setForm,
  form,
  hostnameOptions,
  getSuggestions,
  mode,
  isCaseInsensitive,
  preventOverflow,
  ...conflictProps
}) => {
  const flags = useFlags();
  const isFullstoryManaged = !form.isUserDefined;

  const handleURLRuleChange = useCallback(
    (values: PatternRuleForm[]) => {
      setForm({
        ...form,
        definition: {
          ...form.definition,
          value: [
            // preserve any non pattern definition values, if exist
            // NB: this is the legacy flow not intended to handle merged definitions, we are okay with shifting here as it shouldn't be hit in practice
            ...form.definition.value.filter(v => v.type !== 'pattern'),
            // and then append the validated patterns
            ...validatePatterns(values, flags),
          ],
        },
      });
    },
    [form, setForm, flags],
  );

  const patterns = useMemo(
    () => form.definition.value.filter((v): v is PatternRuleForm => v.type === 'pattern'),
    [form.definition.value],
  );

  return (
    <URLRulesPatterns
      value={patterns}
      onChange={handleURLRuleChange}
      hostnameOptions={hostnameOptions}
      getSuggestions={getSuggestions}
      isML={isFullstoryManaged}
      isCaseInsensitive={isCaseInsensitive}
      preventOverflow={preventOverflow}
      {...conflictProps}
    />
  );
};
